import {ChangeDetectionStrategy, Component, Injectable, OnInit, signal} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {i18n, Oversettelser} from 'felles-ng2/tjenester/i18n/Oversettelser';
import {SpørreskjemaAdministrasjonModell, SpørreskjemaAdministrasjonRessursClient, SpørreskjemaId, Spørsmålstype} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {BekreftKlikkDirektiv} from 'web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk';
import {HtmlFormatertKomponent} from 'web-ng2/delt/komponenter/html-formatert/html-formatert';
import {AdministrasjonNavigasjonKomponent} from '../administrasjon-navigasjon/administrasjon-navigasjon';
import {SpørreskjemaForhåndsvisningKomponent} from '../spørreskjema-forhåndsvisning/spørreskjema-forhåndsvisning';
import {SpørreskjemaRedigerKomponent} from '../spørreskjema-rediger/spørreskjema-rediger';

/** Maks antall avtalenavn som vises for skjema begrenset til avtaler */
const maksAntallAvtalenavn = 3;

/** Skjema som redigeres: 'nytt' ved opprettelse av nytt skjema, null når listen vises */
type Redigering = SpørreskjemaAdministrasjonModell | 'nytt' | null;

/**
 * Administrasjon av spørreskjemaer: oversikt over alle skjemaer, med opprettelse, redigering, kopiering og sletting.
 * Kun tilgjengelig for systemadministratorer.
 */
@Component({
    selector: 'administrasjon-spoerreskjemaer',
    templateUrl: 'administrasjon-spørreskjemaer.html',
    styleUrl: 'administrasjon-spørreskjemaer.scss',
    host: {'data-testid': 'administrasjon-spoerreskjemaer'},
    imports: [
        MatButton,
        MatIcon,
        MatIconButton,
        MatTooltip,
        TranslateModule,
        BekreftKlikkDirektiv,
        HtmlFormatertKomponent,
        AdministrasjonNavigasjonKomponent,
        SpørreskjemaForhåndsvisningKomponent,
        SpørreskjemaRedigerKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdministrasjonSpørreskjemaerKomponent implements OnInit {
    static ajsSelector = 'administrasjonSpoerreskjemaer';

    readonly skjemaer = signal<SpørreskjemaAdministrasjonModell[]>([]);
    readonly redigering = signal<Redigering>(null);
    /** Skjemaer det vises forhåndsvisning av */
    readonly ekspanderte = signal<ReadonlySet<SpørreskjemaId>>(new Set());

    constructor(private avhengigheter: AdministrasjonSpørreskjemaerAvhengigheter, private oversettelser: Oversettelser) {}

    ngOnInit(): void {
        void this.lastSkjemaer();
    }

    erEkspandert(skjema: SpørreskjemaAdministrasjonModell): boolean {
        return this.ekspanderte().has(skjema.id);
    }

    /** Tekst for ekspander-knappen, i18n-nøkkel som oversettes i malen */
    ekspanderTekst(skjema: SpørreskjemaAdministrasjonModell): string {
        return this.erEkspandert(skjema) ? i18n('Skjul forhåndsvisning') : i18n('Vis forhåndsvisning');
    }

    /** Viser eller skjuler forhåndsvisning av skjemaet */
    vekslForhaandsvisning(skjema: SpørreskjemaAdministrasjonModell): void {
        const ekspanderte = new Set(this.ekspanderte());
        if (!ekspanderte.delete(skjema.id)) ekspanderte.add(skjema.id);
        this.ekspanderte.set(ekspanderte);
    }

    nyttSkjema(): void {
        this.redigering.set('nytt');
    }

    rediger(skjema: SpørreskjemaAdministrasjonModell): void {
        this.redigering.set(skjema);
    }

    /** Skjemaet som redigeres, null for nytt skjema */
    redigertSkjema(): SpørreskjemaAdministrasjonModell | null {
        const redigering = this.redigering();
        return redigering === 'nytt' ? null : redigering;
    }

    erIRedigering(): boolean {
        return this.redigering() !== null;
    }

    async vedRedigeringFerdig(): Promise<void> {
        this.redigering.set(null);
        await this.lastSkjemaer();
    }

    /** Oppretter kopi av skjemaet og åpner kopien for redigering */
    async kopier(skjema: SpørreskjemaAdministrasjonModell): Promise<void> {
        const tittel = this.oversettelser.oversett(i18n('Kopi av {tittel}'), {tittel: skjema.tittel});
        const kopi = await this.avhengigheter.kopier(skjema.id, tittel);
        await this.lastSkjemaer();
        this.redigering.set(kopi);
    }

    /** Deaktiverte skjemaer kan ikke benyttes i nye undersøkelser, eksisterende undersøkelser påvirkes ikke */
    async deaktiver(skjema: SpørreskjemaAdministrasjonModell): Promise<void> {
        await this.avhengigheter.deaktiver(skjema.id);
        await this.lastSkjemaer();
    }

    async aktiver(skjema: SpørreskjemaAdministrasjonModell): Promise<void> {
        await this.avhengigheter.aktiver(skjema.id);
        await this.lastSkjemaer();
    }

    async slett(skjema: SpørreskjemaAdministrasjonModell): Promise<void> {
        await this.avhengigheter.slett(skjema.id);
        await this.lastSkjemaer();
    }

    /** Skjema som er i bruk av en eller flere undersøkelser kan ikke slettes */
    erIBruk(skjema: SpørreskjemaAdministrasjonModell): boolean {
        return skjema.antallUndersoekelser > 0;
    }

    /** Navn på avtalene skjemaet er begrenset til, maks 3 navn vises */
    avtalenavn(skjema: SpørreskjemaAdministrasjonModell): string {
        const navn = skjema.avtaler.map(avtale => avtale.navn);
        if (navn.length <= maksAntallAvtalenavn) return navn.join(', ');
        return this.oversettelser.oversett(i18n('{avtaler} og {antall} til'), {avtaler: navn.slice(0, maksAntallAvtalenavn).join(', '), antall: navn.length - maksAntallAvtalenavn});
    }

    /** Antall spørsmål som kan besvares, dvs uten grupper */
    antallSpoersmaal(skjema: SpørreskjemaAdministrasjonModell): number {
        return skjema.spoersmaal.filter(spørsmål => spørsmål.type !== Spørsmålstype.grupperingstittel).length;
    }

    private async lastSkjemaer(): Promise<void> {
        this.skjemaer.set(await this.avhengigheter.finnAlle());
    }
}

@Injectable()
export class AdministrasjonSpørreskjemaerAvhengigheter {
    constructor(private ressurs: SpørreskjemaAdministrasjonRessursClient) {}

    finnAlle(): Promise<SpørreskjemaAdministrasjonModell[]> {
        return this.ressurs.finnAlle().somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke hente spørreskjemaer'));
    }

    kopier(id: SpørreskjemaId, tittel: string): Promise<SpørreskjemaAdministrasjonModell> {
        return this.ressurs.kopier(id, {tittel}).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke kopiere spørreskjemaet'));
    }

    deaktiver(id: SpørreskjemaId): Promise<SpørreskjemaAdministrasjonModell> {
        return this.ressurs.deaktiver(id).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke deaktivere spørreskjemaet'));
    }

    aktiver(id: SpørreskjemaId): Promise<SpørreskjemaAdministrasjonModell> {
        return this.ressurs.aktiver(id).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke aktivere spørreskjemaet'));
    }

    slett(id: SpørreskjemaId): Promise<void> {
        return this.ressurs.slett(id).somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke slette spørreskjemaet')).then(() => undefined);
    }
}
